import React, { useMemo, useState } from 'react';
import { Select, Stack, Text } from '@mantine/core';
import {
  MEDIA_KINDS,
  normalizeMediaSource,
} from '../../media/mediaSource';
import { detectEmbedProvider } from '../../media/embedProviders';
import { variableImageProp } from '../../utils/variableNodes';
import {
  imageFormProp, MEDIA_OBJECT_SLOT, slotForm, slotMeta, withSlotMedia,
} from '../../utils/imageSlots';
import ImageUploadField from './ImageUploadField';
import MediaUploadField from './MediaUploadField';
import { MediaPlaybackFields } from './MediaPlaybackFields';

/* Un seul choix « Vidéo » : fichier vs YouTube/Vimeo est détecté depuis
   l'URL (comme le bloc Vidéo) — le kind stocké (video/embed) reste dérivé. */
const KIND_OPTIONS = [
  { label: 'Image', value: MEDIA_KINDS.image },
  { label: 'Vidéo', value: MEDIA_KINDS.video },
];

/* L'objet média porte sa variable du CRM comme les props d'un bloc portent la
   leur (`srcVariable` à côté de `src`) : `resolveVariableNodesDeep` la
   recopie dans `src` sans connaître le bloc. */
const VARIABLE_KEY = variableImageProp('src');

function patchMedia(current, patch) {
  return normalizeMediaSource({ ...normalizeMediaSource(current), ...patch });
}

const MediaSourceField = ({
  value,
  onChange,
  fieldLabel,
  allowVideo = true,
  mediaFit = 'cover',
  allowEmbed = true,
  showPlayback = true,
}) => {
  const [error, setError] = useState('');
  const media = useMemo(() => normalizeMediaSource(value), [value]);

  const kindOptions = allowVideo
    ? KIND_OPTIONS
    : KIND_OPTIONS.filter((opt) => opt.value === MEDIA_KINDS.image);
  const isVideoLike = media.kind === MEDIA_KINDS.video || media.kind === MEDIA_KINDS.embed;
  /** Valeur du select : les kinds video/embed s'affichent tous deux « Vidéo ». */
  const selectedKind = isVideoLike ? MEDIA_KINDS.video : MEDIA_KINDS.image;

  const setKind = (kind) => {
    if (kind === selectedKind) return;
    const toVideo = kind === MEDIA_KINDS.video;
    const embedProvider = toVideo ? detectEmbedProvider(media.src) : null;
    onChange(patchMedia(media, {
      kind: toVideo ? (embedProvider ? MEDIA_KINDS.embed : MEDIA_KINDS.video) : MEDIA_KINDS.image,
      provider: embedProvider || '',
      playback: toVideo
        ? { controls: true, autoplay: false, loop: false, muted: false }
        : media.playback,
      // Une vidéo ne vient pas du CRM : seules les images ont une variable.
      ...(toVideo ? { [VARIABLE_KEY]: null } : {}),
    }));
  };

  /** URL vidéo (upload ou lien) : kind/provider dérivés de l'URL. */
  const setVideoSrc = ({ url, mediaId, resolution, srcset, poster }) => {
    const embedProvider = detectEmbedProvider(url);
    if (embedProvider && !allowEmbed) {
      setError('Choisissez une vidéo fichier ; les lecteurs externes ne sont pas disponibles ici.');
      return;
    }
    setError('');
    onChange(patchMedia(media, {
      src: url,
      kind: embedProvider ? MEDIA_KINDS.embed : MEDIA_KINDS.video,
      provider: embedProvider || '',
      mediaId,
      resolution,
      srcset,
      poster: poster ?? (url === media.src ? media.poster : ''),
    }));
  };

  /* Les paliers voyagent dans l'objet média lui-même : il porte déjà tout ce
     qui décrit la source, il n'y a pas de prop voisine à inventer. */
  const setImageSrc = (next) => onChange(withSlotMedia(media, MEDIA_OBJECT_SLOT, next));

  const mediaMeta = slotMeta(media, MEDIA_OBJECT_SLOT);
  const setImageForm = (form) => onChange(patchMedia(media, { [imageFormProp('src')]: form }));

  const patchPlayback = (playback) => onChange(patchMedia(media, { playback }));

  return (
    <Stack gap="xs">
      {kindOptions.length > 1 && (
        <Select
          label="Type de média"
          size="xs"
          data={kindOptions}
          value={selectedKind}
          onChange={(v) => v && setKind(v)}
        />
      )}

      {media.kind === MEDIA_KINDS.image && (
        <ImageUploadField
          value={media.src}
          onChange={(url) => setImageSrc({ url })}
          onMediaChange={setImageSrc}
          meta={mediaMeta}
          form={slotForm(media, MEDIA_OBJECT_SLOT)}
          onFormChange={setImageForm}
          fieldLabel={fieldLabel || 'Image'}
          focalPoint={media.focalPoint}
          onFocalPointChange={mediaFit === 'contain' ? undefined
            : (focalPoint) => onChange(patchMedia(media, { focalPoint }))}
        />
      )}

      {isVideoLike && (
        <>
          <MediaUploadField
            kind="video"
            value={media.src}
            onChange={(url) => setVideoSrc({ url })}
            onMediaChange={setVideoSrc}
            meta={mediaMeta}
            fieldLabel={fieldLabel || 'Vidéo'}
          />
          {/* {media.kind === MEDIA_KINDS.video && (
            <TextInput
              label="Image poster (URL)"
              size="xs"
              value={media.poster || ''}
              onChange={(e) => onChange(patchMedia(media, { poster: e.currentTarget.value }))}
              placeholder="Aperçu éditeur + avant lecture"
            />
          )} */}
          {showPlayback && <MediaPlaybackFields playback={media.playback} onPatch={patchPlayback} />}
          {error && <Text role="alert" size="xs" c="red">{error}</Text>}
        </>
      )}
    </Stack>
  );
};

export default MediaSourceField;
